import * as THREE from 'three'
import { Mob } from './Mob'
import { HostileMob } from './HostileMobs'
import Terrain, { BlockType } from '../terrain'
import { InventoryItemType, ItemType } from '../items'

const px = 0.0625

const createMat = (color: number) =>
  new THREE.MeshStandardMaterial({
    color,
    roughness: 0.92,
    metalness: 0
  })

const makeBox = (
  width: number,
  height: number,
  depth: number,
  material: THREE.Material
) => {
  const geometry = new THREE.BoxGeometry(px * width, px * height, px * depth)
  return new THREE.Mesh(geometry, material)
}

const makeLeg = (
  width: number,
  height: number,
  depth: number,
  material: THREE.Material
) => {
  const leg = makeBox(width, height, depth, material)
  leg.geometry.translate(0, -px * height * 0.5, 0)
  return leg
}

const enableShadows = (mesh: THREE.Group) => {
  mesh.traverse(object => {
    if (object instanceof THREE.Mesh) {
      object.castShadow = true
      object.receiveShadow = true
    }
  })
}

// Passive Sprout Mole
export class SproutMole extends Mob {
  legFL: THREE.Mesh
  legFR: THREE.Mesh
  legBL: THREE.Mesh
  legBR: THREE.Mesh
  headBody: THREE.Mesh
  stem: THREE.Mesh
  leaf1: THREE.Mesh
  leaf2: THREE.Mesh
  animationPhase = 0

  constructor(scene: THREE.Scene, terrain: Terrain, position: THREE.Vector3) {
    super(scene, terrain, position)
    this.speed = 1.0
    this.maxHealth = 8
    this.health = this.maxHealth
    this.bodyRadius = 0.3

    const moleColor = 0xe9dfcb // beige skin
    const sproutColor = 0x8edb7a // bright green leaf
    const eyeColor = 0x2b2b2b // dark grey eye
    const cheekColor = 0xffa3b1 // pink blush cheeks

    const bodyMat = createMat(moleColor)
    const sproutMat = createMat(sproutColor)
    const eyeMat = createMat(eyeColor)
    const cheekMat = createMat(cheekColor)

    // Body/Head is a unified box: 9w x 9h x 9d
    this.headBody = makeBox(9, 9, 9, bodyMat)
    this.headBody.position.set(0, px * 7.5, 0)
    this.mesh.add(this.headBody)
    this.headPart = this.headBody

    // Sprout stem: 1w x 3h x 1d
    this.stem = makeBox(1, 3, 1, sproutMat)
    this.stem.position.set(0, px * 6, 0)
    this.headBody.add(this.stem)

    // Sprout leaf 1: 3w x 1h x 2d
    this.leaf1 = makeBox(3, 1, 2, sproutMat)
    this.leaf1.position.set(px * 1.5, px * 1.5, 0)
    this.leaf1.rotation.z = -0.2
    this.stem.add(this.leaf1)

    // Sprout leaf 2: 3w x 1h x 2d
    this.leaf2 = makeBox(3, 1, 2, sproutMat)
    this.leaf2.position.set(-px * 1.5, px * 1.5, 0)
    this.leaf2.rotation.z = 0.2
    this.stem.add(this.leaf2)

    // Black eyes: 1.5w x 1.5h x 0.2d
    const eyeL = makeBox(1.5, 1.5, 0.2, eyeMat)
    const eyeR = makeBox(1.5, 1.5, 0.2, eyeMat)
    eyeL.position.set(px * 2.2, px * 1.5, px * 4.6)
    eyeR.position.set(-px * 2.2, px * 1.5, px * 4.6)
    this.headBody.add(eyeL, eyeR)

    // Blush cheeks: 1.5w x 1h x 0.2d
    const cheekL = makeBox(1.5, 1.0, 0.2, cheekMat)
    const cheekR = makeBox(1.5, 1.0, 0.2, cheekMat)
    cheekL.position.set(px * 3.4, px * 0.0, px * 4.6)
    cheekR.position.set(-px * 3.4, px * 0.0, px * 4.6)
    this.headBody.add(cheekL, cheekR)

    // Legs: 2.5w x 3h x 2.5d each
    this.legFL = makeLeg(2.5, 3, 2.5, bodyMat)
    this.legFR = makeLeg(2.5, 3, 2.5, bodyMat)
    this.legBL = makeLeg(2.5, 3, 2.5, bodyMat)
    this.legBR = makeLeg(2.5, 3, 2.5, bodyMat)

    this.legFL.position.set(px * 2.8, px * 3, px * 2.8)
    this.legFR.position.set(-px * 2.8, px * 3, px * 2.8)
    this.legBL.position.set(px * 2.8, px * 3, -px * 2.8)
    this.legBR.position.set(-px * 2.8, px * 3, -px * 2.8)

    this.mesh.add(this.legFL, this.legFR, this.legBL, this.legBR)

    enableShadows(this.mesh)
  }

  updateAnimation(delta: number): void {
    if (this.state === 'walking') {
      this.animationPhase += delta * 10
      const swing = Math.sin(this.animationPhase) * 0.75
      this.legFL.rotation.x = swing
      this.legBL.rotation.x = -swing
      this.legFR.rotation.x = -swing
      this.legBR.rotation.x = swing

      this.stem.rotation.z = Math.sin(this.animationPhase * 0.5) * 0.1
      this.stem.rotation.x = Math.cos(this.animationPhase * 0.5) * 0.08
    } else {
      const lerpFactor = delta * 6
      this.legFL.rotation.x = THREE.MathUtils.lerp(this.legFL.rotation.x, 0, lerpFactor)
      this.legFR.rotation.x = THREE.MathUtils.lerp(this.legFR.rotation.x, 0, lerpFactor)
      this.legBL.rotation.x = THREE.MathUtils.lerp(this.legBL.rotation.x, 0, lerpFactor)
      this.legBR.rotation.x = THREE.MathUtils.lerp(this.legBR.rotation.x, 0, lerpFactor)
      this.stem.rotation.x = THREE.MathUtils.lerp(this.stem.rotation.x, 0, lerpFactor)
      this.stem.rotation.z = THREE.MathUtils.lerp(this.stem.rotation.z, 0, lerpFactor)
    }
  }

  getDrops(): InventoryItemType[] {
    const drops: InventoryItemType[] = []
    if (Math.random() < 0.6) {
      drops.push(ItemType.cookedBeef) // Tofu representation
    }
    if (Math.random() < 0.5) {
      drops.push(ItemType.stick)
    }
    return drops
  }
}

// Hostile Sprout Mole
export class HostileSproutMole extends HostileMob {
  legFL: THREE.Mesh
  legFR: THREE.Mesh
  legBL: THREE.Mesh
  legBR: THREE.Mesh
  headBody: THREE.Mesh
  stem: THREE.Mesh
  leaf1: THREE.Mesh
  leaf2: THREE.Mesh
  animationPhase = 0

  constructor(scene: THREE.Scene, terrain: Terrain, position: THREE.Vector3) {
    super(scene, terrain, position)
    this.speed = 1.6
    this.maxHealth = 10
    this.health = this.maxHealth
    this.attackDamage = 1
    this.attackCooldown = 1.0
    this.bodyRadius = 0.3

    const moleColor = 0xd7cbac // slightly angrier tan/beige skin
    const sproutColor = 0x8edb7a // bright green leaf
    const eyeColor = 0xff2a2a // glowing red angry eyes
    const cheekColor = 0xcca3b1 // faded cheeks

    const bodyMat = createMat(moleColor)
    const sproutMat = createMat(sproutColor)
    const eyeMat = new THREE.MeshStandardMaterial({
      color: eyeColor,
      emissive: eyeColor,
      emissiveIntensity: 0.6,
      roughness: 0.9,
      metalness: 0
    })
    const cheekMat = createMat(cheekColor)

    // Body/Head is a unified box: 9w x 9h x 9d
    this.headBody = makeBox(9, 9, 9, bodyMat)
    this.headBody.position.set(0, px * 7.5, 0)
    this.mesh.add(this.headBody)
    this.headPart = this.headBody

    // Sprout stem: 1w x 3h x 1d
    this.stem = makeBox(1, 3, 1, sproutMat)
    this.stem.position.set(0, px * 6, 0)
    this.headBody.add(this.stem)

    // Sprout leaf 1: 3w x 1h x 2d
    this.leaf1 = makeBox(3, 1, 2, sproutMat)
    this.leaf1.position.set(px * 1.5, px * 1.5, 0)
    this.leaf1.rotation.z = -0.2
    this.stem.add(this.leaf1)

    // Sprout leaf 2: 3w x 1h x 2d
    this.leaf2 = makeBox(3, 1, 2, sproutMat)
    this.leaf2.position.set(-px * 1.5, px * 1.5, 0)
    this.leaf2.rotation.z = 0.2
    this.stem.add(this.leaf2)

    // Red angry eyes: 1.5w x 1.5h x 0.2d, slanted for angry expression!
    const eyeL = makeBox(1.5, 1.5, 0.2, eyeMat)
    const eyeR = makeBox(1.5, 1.5, 0.2, eyeMat)
    eyeL.position.set(px * 2.2, px * 1.8, px * 4.6)
    eyeR.position.set(-px * 2.2, px * 1.8, px * 4.6)
    eyeL.rotation.z = 0.3
    eyeR.rotation.z = -0.3
    this.headBody.add(eyeL, eyeR)

    // Blush cheeks: 1.5w x 1h x 0.2d
    const cheekL = makeBox(1.5, 1.0, 0.2, cheekMat)
    const cheekR = makeBox(1.5, 1.0, 0.2, cheekMat)
    cheekL.position.set(px * 3.4, px * 0.0, px * 4.6)
    cheekR.position.set(-px * 3.4, px * 0.0, px * 4.6)
    this.headBody.add(cheekL, cheekR)

    // Legs: 2.5w x 3h x 2.5d each
    this.legFL = makeLeg(2.5, 3, 2.5, bodyMat)
    this.legFR = makeLeg(2.5, 3, 2.5, bodyMat)
    this.legBL = makeLeg(2.5, 3, 2.5, bodyMat)
    this.legBR = makeLeg(2.5, 3, 2.5, bodyMat)

    this.legFL.position.set(px * 2.8, px * 3, px * 2.8)
    this.legFR.position.set(-px * 2.8, px * 3, px * 2.8)
    this.legBL.position.set(px * 2.8, px * 3, -px * 2.8)
    this.legBR.position.set(-px * 2.8, px * 3, -px * 2.8)

    this.mesh.add(this.legFL, this.legFR, this.legBL, this.legBR)

    enableShadows(this.mesh)
  }

  updateAnimation(delta: number): void {
    if (this.state === 'walking') {
      this.animationPhase += delta * 14
      const swing = Math.sin(this.animationPhase) * 0.8
      this.legFL.rotation.x = swing
      this.legBL.rotation.x = -swing
      this.legFR.rotation.x = -swing
      this.legBR.rotation.x = swing

      this.stem.rotation.z = Math.sin(this.animationPhase * 0.5) * 0.15
      this.stem.rotation.x = Math.cos(this.animationPhase * 0.5) * 0.1
    } else {
      const lerpFactor = delta * 6
      this.legFL.rotation.x = THREE.MathUtils.lerp(this.legFL.rotation.x, 0, lerpFactor)
      this.legFR.rotation.x = THREE.MathUtils.lerp(this.legFR.rotation.x, 0, lerpFactor)
      this.legBL.rotation.x = THREE.MathUtils.lerp(this.legBL.rotation.x, 0, lerpFactor)
      this.legBR.rotation.x = THREE.MathUtils.lerp(this.legBR.rotation.x, 0, lerpFactor)
      this.stem.rotation.x = THREE.MathUtils.lerp(this.stem.rotation.x, 0, lerpFactor)
      this.stem.rotation.z = THREE.MathUtils.lerp(this.stem.rotation.z, 0, lerpFactor)
    }
  }

  getDrops(): InventoryItemType[] {
    const drops: InventoryItemType[] = []
    if (Math.random() < 0.6) {
      drops.push(ItemType.cookedBeef) // Tofu!
    }
    if (Math.random() < 0.5) {
      drops.push(ItemType.stick)
    }
    return drops
  }
}

// Something Shadow Entity
export class Something extends HostileMob {
  bodyGroup: THREE.Group
  eye: THREE.Mesh
  pupil: THREE.Mesh
  hairStrands: THREE.Mesh[] = []
  animationPhase = 0

  constructor(scene: THREE.Scene, terrain: Terrain, position: THREE.Vector3) {
    super(scene, terrain, position)
    this.speed = 0.95
    this.maxHealth = 24
    this.health = this.maxHealth
    this.attackDamage = 3
    this.attackCooldown = 1.8
    this.bodyRadius = 0.5

    const shadowColor = 0x050505
    const eyeWhiteColor = 0xeeeeee
    const eyePupilColor = 0x990000 // glowing red pupil

    const shadowMat = createMat(shadowColor)
    const eyeWhiteMat = createMat(eyeWhiteColor)
    const pupilMat = new THREE.MeshStandardMaterial({
      color: eyePupilColor,
      emissive: eyePupilColor,
      emissiveIntensity: 0.8,
      roughness: 0.9,
      metalness: 0
    })

    this.bodyGroup = new THREE.Group()
    this.mesh.add(this.bodyGroup)

    // Main flat outline body: 12w x 26h x 2d
    const body = makeBox(12, 26, 2, shadowMat)
    body.position.set(0, px * 15, 0)
    this.bodyGroup.add(body)

    // Large single eye in center: 5w x 3.5h x 0.4d
    this.eye = makeBox(5, 3.5, 0.4, eyeWhiteMat)
    this.eye.position.set(0, px * 16, px * 1.1)
    this.bodyGroup.add(this.eye)

    // Pupil: 1.5w x 1.5h x 0.2d
    this.pupil = makeBox(1.5, 1.5, 0.2, pupilMat)
    this.pupil.position.set(0, 0, px * 0.25)
    this.eye.add(this.pupil)

    // Add hair strands / tentacles hanging from body bottom
    const strandL = makeLeg(2, 8, 1.5, shadowMat)
    strandL.position.set(px * -4, px * 3, 0)
    this.bodyGroup.add(strandL)
    this.hairStrands.push(strandL)

    const strandR = makeLeg(2, 8, 1.5, shadowMat)
    strandR.position.set(px * 4, px * 3, 0)
    this.bodyGroup.add(strandR)
    this.hairStrands.push(strandR)

    const strandC = makeLeg(2, 5, 1.5, shadowMat)
    strandC.position.set(0, px * 3, px * 0.2)
    this.bodyGroup.add(strandC)
    this.hairStrands.push(strandC)

    enableShadows(this.mesh)
  }

  updateAnimation(delta: number): void {
    this.animationPhase += delta * 1.8

    // Floating hover translation
    this.bodyGroup.position.y = Math.sin(this.animationPhase) * 0.2

    // Creepy hair strand sways
    const sway = Math.sin(this.animationPhase * 1.5) * 0.15
    this.hairStrands[0].rotation.z = sway
    this.hairStrands[1].rotation.z = -sway
    this.hairStrands[2].rotation.x = Math.cos(this.animationPhase) * 0.1

    // Red pupil twitches occasionally
    if (Math.random() < 0.02) {
      this.pupil.position.x = (Math.random() - 0.5) * px * 1.5
      this.pupil.position.y = (Math.random() - 0.5) * px * 1.0
    } else {
      this.pupil.position.x = THREE.MathUtils.lerp(this.pupil.position.x, 0, delta * 8)
      this.pupil.position.y = THREE.MathUtils.lerp(this.pupil.position.y, 0, delta * 8)
    }
  }

  getDrops(): InventoryItemType[] {
    const drops: InventoryItemType[] = []
    if (Math.random() < 0.5) {
      drops.push(BlockType.obsidian)
    }
    const coalCount = 1 + Math.floor(Math.random() * 2)
    for (let i = 0; i < coalCount; i++) {
      drops.push(BlockType.coal)
    }
    return drops
  }
}
